import { useIntl } from "react-intl";
import cn from "classnames";

// MUI Components
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    ListItem,
    Pagination,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { FilterOptionsPill } from "components/FilterOptionsPill";
import NavButton from "components/HeaderNavigation/NavButton";
import { Checkbox } from "components/Checkbox";
import { SelectedDataList } from "../SelectedDataList";
import { ButtonWithCounter } from "components/Buttons";
import { SnackBar } from "components/SnackBar";
import { ListOfEntities } from "components/SnackBar/ListOfEntities/ListOfEntities";
import { TruncatedText } from "components/TruncatedText";

// Hooks
import useTeamsModal from "./useTeamsModal";

// Icons
import icons from "enums/icons";
import CloseIcon from "@mui/icons-material/Close";

// Styles
import teamsModalStyles from "./TeamsModal.styles";

const TeamsModal = ({ onClose }: { onClose: () => void }) => {
    const intl = useIntl();
    const classes = teamsModalStyles();

    const {
        people,
        teams,
        requestStatus,
        shouldShowAddTeamBtn,
        expandSelected,
        mode,
        inputNameValue,
        inputTeamValue,
        filteredTeams,
        newTeam,
        usersOnTeam,
        isSomePeopleSelected,
        isAllPeopleSelected,
        disabledSaveButton,
        setExpandSelected,
        switchToEditTeamMode,
        shouldOpenSnackBar,
        popOverAnchorEl,
        handleGoBack,
        handleFilter,
        handleAddNewTeam,
        handleCheck,
        handleCheckAll,
        handleDeleteSelected,
        handleRemoveAllSelected,
        handleSaveChanges,
        handlePageChange,
        handleClickCounter,
        handleCloseCounter,
    } = useTeamsModal();

    return (
        <Box
            className={cn(
                classes.mainWrapper,
                { [classes.mainWrapperNarrow]: !mode },
                { [classes.mainWrapperWide]: mode }
            )}
        >
            {requestStatus === "fetching" && (
                <Box
                    sx={{
                        minHeight: "inherit",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {requestStatus === "done" && (
                <>
                    {/* MODAL HEADER */}
                    <Box display="flex" mb={mode ? 1 : 2} justifyContent="space-between" alignItems="center">
                        {/* TITLE */}
                        <Typography sx={{ fontSize: 20, fontWeight: 600, color: "text-primary" }}>
                            {mode === "ADD" &&
                                intl.formatMessage({ id: "behaviouralComparison.teamsModal.addTeamTitle" })}
                            {mode === "EDIT" &&
                                intl.formatMessage({ id: "behaviouralComparison.teamsModal.editTeamTitle" })}
                            {mode === undefined &&
                                intl.formatMessage({ id: "behaviouralComparison.teamsModal.mainTitle" })}
                        </Typography>

                        {/* HEADER BUTTONS */}
                        {mode ? (
                            <NavButton
                                sx={{ height: "auto" }}
                                onClick={handleGoBack}
                                isBackButton
                                variant="text"
                                translationId="behaviouralComparison.teamdModal.goBackButton"
                            />
                        ) : (
                            <IconButton sx={{ p: 0 }} onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Box>

                    {/* DESCRIPTION */}
                    <Box display="flex" alignItems="center" mb={1}>
                        <Typography
                            sx={{ fontSize: mode ? 16 : 14, fontWeight: 500, color: "secondary.main", mr: 1 }}
                            variant="subtitle1"
                        >
                            {mode === "ADD" &&
                                intl.formatMessage({ id: "behaviouralComparison.teamsModal.addTeam.description" })}
                            {mode === "EDIT" &&
                                intl.formatMessage({ id: "behaviouralComparison.teamsModal.editTeam.description" })}
                            {mode === undefined &&
                                intl.formatMessage({ id: "behaviouralComparison.teamsModal.mainDescription" })}
                        </Typography>

                        {mode && (
                            <FilterOptionsPill
                                filterId="team"
                                label={
                                    mode === "ADD" ? (
                                        <TruncatedText sx={{ fontSize: 12, minWidth: 0 }}>
                                            {newTeam ? newTeam.name : ""}
                                        </TruncatedText>
                                    ) : (
                                        <TruncatedText sx={{ fontSize: 12, minWidth: 0 }}>
                                            {teams.selected[0] ? teams.selected[0].name : ""}
                                        </TruncatedText>
                                    )
                                }
                            />
                        )}
                    </Box>

                    {/* SEARCH BAR */}
                    <TextField
                        fullWidth
                        size="small"
                        id="team-search"
                        name="team-search"
                        autoComplete="off"
                        value={inputNameValue || inputTeamValue}
                        className={classes.searchBar}
                        onChange={handleFilter}
                        placeholder={
                            mode
                                ? intl.formatMessage({
                                      id: "behaviouralComparison.teamsModal.searchUsers.placeholder",
                                  })
                                : intl.formatMessage({
                                      id: "behaviouralComparison.teamsModal.searchTeam.placeholder",
                                  })
                        }
                        inputProps={{
                            maxLength: 30,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CustomIcon icon={icons.searchInverted} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="start">
                                    {shouldShowAddTeamBtn && (
                                        <Button
                                            variant="text"
                                            className={classes.addTeamButton}
                                            onClick={handleAddNewTeam}
                                        >
                                            {intl.formatMessage({ id: "userManagement.setTeam.label.addTeam" })}
                                        </Button>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* PEOPLE AND TEAMS LISTS */}
                    {mode ? (
                        <>
                            <SelectedDataList
                                type="users"
                                selectedItems={people.selected}
                                allSelected={isAllPeopleSelected}
                                someSelected={isSomePeopleSelected}
                                expandSelected={expandSelected}
                                setExpandSelected={setExpandSelected}
                                handleCheckAll={(ev) => handleCheckAll(ev.target.checked)}
                                handleRemoveAllSelected={handleRemoveAllSelected}
                                handleDeleteSelected={handleDeleteSelected}
                            />
                            <Divider sx={{ color: "neutrals.light", margin: "8px 0px 12px 0px" }} />

                            <Box sx={{ mb: 2 }}>
                                <Grid container spacing={2}>
                                    {people.data?.map((person) => (
                                        <Grid item xs={3} key={person.id}>
                                            <FormControlLabel
                                                sx={{
                                                    m: 0,
                                                    "& .MuiTypography-root": {
                                                        fontSize: 14,
                                                        letterSpacing: 0.25,
                                                    },
                                                    "& .MuiCheckbox-root": {
                                                        padding: 0,
                                                        mr: 0.5,
                                                    },
                                                }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        className={classes.checkboxes}
                                                        checked={person.checked}
                                                        onChange={(ev) => handleCheck(ev.target.checked, person.id)}
                                                    />
                                                }
                                                label={person.name}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            {filteredTeams?.map((team) => (
                                <Box key={team.id}>
                                    <ListItem disablePadding sx={{ mb: 1 }}>
                                        <IconButton size="small" onClick={() => switchToEditTeamMode(team)}>
                                            <CustomIcon
                                                icon={icons.pencil}
                                                sx={{ fontSize: 16, color: "secondary.main" }}
                                            />
                                        </IconButton>

                                        <FilterOptionsPill
                                            filterId="team"
                                            label={
                                                <Box className={classes.teamPillLabel}>
                                                    <TruncatedText sx={{ fontSize: 12, minWidth: 0, mr: 1 }}>
                                                        {team.name}
                                                    </TruncatedText>

                                                    <ButtonWithCounter
                                                        size={22}
                                                        fontSize={10}
                                                        count={team.count}
                                                        shouldShowIcon={teams?.selected?.[0]?.id === team.id}
                                                        colors={["hovered", "info"]}
                                                        callback={(ev) => handleClickCounter(ev, team)}
                                                    />
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {mode && people.meta.pages > 1 && (
                        <Pagination
                            size="small"
                            className={classes.pagination}
                            count={people.meta.pages}
                            page={people.meta.page_no}
                            onChange={(ev, newPage) => handlePageChange(ev, newPage)}
                        />
                    )}

                    {mode && (
                        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={4}>
                            <Button size="small" variant="text" className={classes.cancelButton} onClick={handleGoBack}>
                                <Typography fontSize={12}>Cancel</Typography>
                            </Button>

                            <Button
                                disabled={!people.selected?.length || disabledSaveButton}
                                variant="contained"
                                sx={{ marginLeft: "8px", "& .MuiButton-startIcon>*:nth-of-type(1)": { fontSize: 12 } }}
                                startIcon={<CustomIcon icon={icons.check} />}
                                onClick={handleSaveChanges}
                            >
                                {mode === "EDIT" ? "Save Changes" : "Save New Team"}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            <SnackBar
                maxWidth={350}
                shouldOpen={shouldOpenSnackBar}
                anchorElement={popOverAnchorEl}
                handleInfoClose={handleCloseCounter}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                content={
                    <ListOfEntities
                        columnNames={["NAME", "ROLE"]}
                        list={usersOnTeam.map((user) => ({
                            id: user.id,
                            term: user.name,
                            value: user.job_title,
                        }))}
                    />
                }
            />
        </Box>
    );
};

export default TeamsModal;
